import React from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text } from "@xsolla/xui-primitives";
import { useResolvedTheme } from "@xsolla/xui-core";
import type { LinkProps, LinkSize } from "./types";

export const Link: React.FC<LinkProps> = ({
  size = "md",
  href,
  onClick,
  disabled = false,
  underline = false,
  children,
  target,
  rel,
  color,
  testID,
  themeMode,
  themeProductContext,
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });

  const fontSizeMap: Record<LinkSize, number> = {
    sm: 12,
    md: 14,
    lg: 16,
  };

  const handleClick = (e: any) => {
    if (disabled) {
      e.preventDefault?.();
      return;
    }
    if (onClick) {
      e.preventDefault?.();
      onClick();
    }
  };

  const getRel = (): string | undefined => {
    if (target === "_blank") {
      const securityRel = "noopener noreferrer";
      if (rel) {
        const relParts = rel.split(/\s+/);
        const securityParts = securityRel.split(/\s+/);
        const allParts = [...relParts, ...securityParts];
        const uniqueParts = Array.from(new Set(allParts));
        return uniqueParts.join(" ");
      }
      return securityRel;
    }
    return rel;
  };

  const defaultColor = theme.colors.control.link.primary;
  const disabledColor = theme.colors.control.text.disable;
  const textColor = disabled ? disabledColor : color || defaultColor;

  return (
    <Box
      as="a"
      href={disabled ? undefined : href}
      onPress={handleClick}
      onClick={handleClick}
      target={target}
      rel={getRel()}
      cursor={disabled ? "not-allowed" : "pointer"}
      flexDirection="row"
      alignItems="center"
      display="inline-flex"
      gap={8}
      testID={testID}
      role="link"
      tabIndex={disabled ? -1 : 0}
      hoverStyle={!disabled ? { opacity: 0.8 } : undefined}
      focusStyle={{
        outlineColor: theme.colors.border.brand,
        outlineWidth: 2,
        outlineStyle: "solid",
        outlineOffset: 2,
      }}
    >
      <Text
        color={textColor}
        fontSize={fontSizeMap[size]}
        fontWeight="400"
        textDecoration={underline ? "underline" : "none"}
      >
        {children}
      </Text>
    </Box>
  );
};

Link.displayName = "Link";
